import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { UnitOfMeasureModal } from '../components/UnitOfMeasureModal';
import { unitsOfMeasureApi } from '../inventoryApi';
import type { UnitOfMeasureListItem } from '../inventoryTypes';

/**
 * The units stock is counted in.
 *
 * **The anchor is the NAME, not the code**, unlike the item catalog. A reader calls this row
 * "Piece"; the code is the short form written on a row, and it keeps its own sortable column.
 * The item list is the one place in this suite where a code anchors, because two items there
 * legitimately share a name — which is not true here.
 */
export function UnitsOfMeasureIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<UnitOfMeasureListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    unit: UnitOfMeasureListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchUnits = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      unitsOfMeasureApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (unit: UnitOfMeasureListItem): MenuItem[] => [
      {
        key: `edit-${unit.id}`,
        label: 'Edit',
        permission: 'units-of-measure.update',
        onClick: () => setEditing(unit),
      },
      {
        key: `restore-${unit.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'units-of-measure.delete',
        disabledReason: unit.isActive ? 'This unit is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', unit }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await unitsOfMeasureApi.deactivate(confirming.unit.id);
      else await unitsOfMeasureApi.activate(confirming.unit.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<UnitOfMeasureListItem>
        module="units-of-measure"
        breadcrumb={[
          'Logistics',
          'Inventory',
          { label: 'Settings', to: '/inventory/settings' },
          'Units of Measure',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['UnitOfMeasure']}
        rowKey={(unit) => unit.id}
        fetch={fetchUnits}
        reloadKey={reloadKey}
        searchPlaceholder="Search units"
        emptyMessage="No units yet. Add the units stock is counted in."
        onCreate={() => setCreating(true)}
        createPermission="units-of-measure.create"
        onRowOpen={(unit) => navigate(`/inventory/settings/units/${unit.id}`)}
        openPermission="units-of-measure.read"
        rowActions={rowActions}
        onDelete={(unit) => setConfirming({ kind: 'deactivate', unit })}
        deletePermission="units-of-measure.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(unit) => (unit.isActive ? null : 'This unit is already deleted.')}
        onClearData={() => unitsOfMeasureApi.clear()}
        clearDataPermission="units-of-measure.clear-data"
        otherInfo={(unit) => unit}
      />

      {creating || editing !== null ? (
        <UnitOfMeasureModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete unit' : 'Restore unit'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`unit-of-measure-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.unit.name}" stops being offered on the item form.`
                  : `"${confirming.unit.name}" is offered again on the item form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.unit.itemCount > 0 ? (
                <p>
                  {confirming.unit.itemCount} item
                  {confirming.unit.itemCount === 1 ? ' is' : 's are'} counted in it, and keep
                  resolving its code. Their stock figures are unaffected — what stops is the unit
                  being offered for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted unit can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<UnitOfMeasureListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (unit) => <TextCell value={unit.name} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 110,
    sortable: true,
    summary: true,
    render: (unit) => <TextCell value={unit.code} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (unit) => <TextCell value={unit.description} />,
  },
  {
    key: 'itemCount',
    header: 'Items',
    width: 90,
    align: 'right',
    sortable: true,
    render: (unit) => <TextCell value={String(unit.itemCount)} mono numeric />,
  },
  {
    // "Order", never "Level": a position is only correct with the whole ladder in hand, and
    // this is a page of records.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (unit) => <TextCell value={String(unit.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (unit) =>
      unit.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
